/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-membership.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import { Checkbox } from '@progress/kendo-react-inputs';
import _ from 'lodash';
import Button from '@components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import Filter from '@components/core/filter';
import ItemList from '@components/core/item-list';
import { useTranslation, useBoolean } from '@hooks/index';
import {
    OrganisationMemberFields,
    StudyFields_Study_members_StudyMember as StudyFields_members,
    OrganisationAndStudyMemberFields_Organisation_studies_Study as OrganisationAndStudyMemberFields_studies,
} from '@queries/generated/fragments.generated';
import { SET_USER_STUDY_MEMBERSHIP } from '@queries/studies';
import { GET_ORGANISATION_AND_STUDY_MEMBERS } from '@queries/uam';

interface StudyRow extends OrganisationAndStudyMemberFields_studies {
    icon?: 'user-cog' | null;
    fal?: boolean;
    isAdmin?: boolean;
    active?: boolean;
}

interface RestrictedStudyRow {
    isAdmin: boolean;
    id: string;
}

const StudyMembership = ({
    onClose,
    open,
    user,
    studies,
    canEdit,
}: {
    onClose: Function;
    open: boolean;
    user: OrganisationMemberFields;
    studies: OrganisationAndStudyMemberFields_studies[];
    canEdit: boolean;
}) => {
    const { t } = useTranslation();

    const [setStudyMembership] = useMutation(SET_USER_STUDY_MEMBERSHIP);
    const [filter, setFilter] = useState('');
    const [selectedStudies, setSelectedStudies] = useState([] as StudyRow[]);
    const [initialSelectedStudies, setInitialSelectedStudies] = useState([] as RestrictedStudyRow[]);
    const [filteredStudies, setFilteredStudies] = useState([] as StudyRow[]);
    const isFirst = useBoolean(true);
    const saving = useBoolean(false);
    const active = useBoolean(false);
    const disabledSave = useBoolean(true);

    const userInfo = user?.user
        ? `${user.user.firstName} ${user.user.lastName} (${user.user.email})`
        : `${user?.firstName || ''} ${user?.lastName || ''} (${user?.email || ''})`;

    const getMember = (item: StudyRow) =>
        item.members.find((member: StudyFields_members) => member.userId === user?.userId);

    const filterByActive = (data: StudyRow[]) =>
        data.filter((item: StudyRow) => {
            const member = getMember(item);
            return member?.active;
        });

    const getStudyRows = (inputStudies: OrganisationAndStudyMemberFields_studies[]) => {
        let data = [] as StudyRow[];
        if (inputStudies?.length) {
            data = orderBy(studies, [{ field: 'studyNumber', dir: 'asc' }]);
            data = data.map((item: StudyRow) => {
                const member = getMember(item);
                return {
                    ...item,
                    icon: member?.active ? 'user-cog' : null,
                    fal: !member?.isAdmin,
                    isAdmin: member?.isAdmin ?? false,
                    active: member?.active ?? false,
                };
            });
        }
        return data;
    };

    const getRestrictedStudyRows = (studyRows: StudyRow[]) =>
        studyRows.map((item: StudyRow) => ({
            isAdmin: item.isAdmin,
            id: item.id,
        }));

    const close = (action?: string | boolean | undefined) => {
        isFirst.setTrue();
        disabledSave.setTrue();
        onClose(action);
        active.setFalse();
    };

    const setDefaultMembers = () => {
        const data = getStudyRows(studies);
        setSelectedStudies(filterByActive(data));

        const initialStudies = filterByActive(data);
        const initialRestrictedStudies = getRestrictedStudyRows(initialStudies);
        setInitialSelectedStudies(initialRestrictedStudies);
    };

    const getAvailableStudies = () => {
        if (studies?.length) {
            let data = orderBy(studies, [{ field: 'studyNumber', dir: 'asc' }]) as StudyRow[];
            data = data
                .filter((item: StudyRow) => {
                    const member = getMember(item);

                    if (active.value) return member?.active;

                    return true;
                })
                .map((item: StudyRow) => {
                    const member = getMember(item);
                    const selectedItem: StudyRow = selectedStudies.length
                        ? selectedStudies.find((study) => study.id === item.id)
                        : null;
                    return {
                        ...item,
                        icon: selectedItem ? 'user-cog' : null,
                        fal: !selectedItem?.isAdmin,
                        isAdmin: selectedItem?.isAdmin === undefined ? member?.isAdmin : selectedItem?.isAdmin,
                        active: selectedItem?.active ?? false,
                    } as StudyRow;
                });
            return data?.filter((item: StudyRow) =>
                !filter.length ? true : item.studyNumber.toUpperCase().indexOf(filter) >= 0,
            );
        }
        return [];
    };

    const activeStudies = filterByActive([...filteredStudies]);

    useEffect(() => {
        if (open) setDefaultMembers();
    }, [open, studies]);

    useEffect(() => {
        if (open) setFilteredStudies(getAvailableStudies());
    }, [filter, active, selectedStudies]);

    const detectChanges = () => {
        const selectedArray = selectedStudies ? getRestrictedStudyRows(selectedStudies) : [];

        if (selectedArray.length !== initialSelectedStudies.length) return true;

        return _.differenceWith(selectedArray, initialSelectedStudies, _.isEqual).length > 0;
    };

    useEffect(() => {
        disabledSave.setValue(!detectChanges());
    }, [initialSelectedStudies, selectedStudies]);

    const assignItems = () => {
        saving.setTrue();
        const ids = selectedStudies.map((item: any) => item.id);
        const isAdmins = selectedStudies.map((item: any) => item.isAdmin || false);

        setStudyMembership({
            variables: {
                userId: user?.userId ?? null,
                studyIds: ids,
                isAdmins,
                reason: '',
            },
            refetchQueries: [
                {
                    query: GET_ORGANISATION_AND_STUDY_MEMBERS,
                },
            ],
        })
            .then((res) => {
                saving.setFalse();
                setSelectedStudies([]);
                close('success');
            })
            .catch((error) => {
                console.log('error', error);
                saving.setFalse();
                close('error');
            });
    };

    const handleIsAdmin = (item: any) => {
        const array = [...selectedStudies];
        const index = array.findIndex((el: any) => el.id === item.id);
        if (index >= 0) {
            array[index].isAdmin = !array[index].isAdmin;
            array[index].fal = !array[index].isAdmin;
        }
        setSelectedStudies(array);
    };

    useEffect(() => {
        if (activeStudies?.length && isFirst.value) {
            isFirst.setFalse();
            setSelectedStudies(activeStudies);
        }
    }, [activeStudies]);

    const isEnabled =
        selectedStudies.length ||
        (selectedStudies.length === 0 && filterByActive(studies).length !== selectedStudies.length);

    return (
        <CustomDialog
            onClose={close}
            aria-labelledby="assign-variables"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
        >
            <DialogTitle id="study-membership-modal-title">
                {t('uam:cmRamStudyMembership')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content pb-0">
                <>
                    {user && (
                        <div className="content-text">
                            <span className="title">{t('user')}:</span>
                            {userInfo}
                        </div>
                    )}
                    <div className="filter-item-mb">
                        <Filter id="study-membership-filter" onChange={({ text }: any) => setFilter(text)} />
                    </div>
                    <div className="list-info">
                        <p className="text-length">
                            {filteredStudies.length} {t('uam:studyRecords')}
                        </p>
                        <Checkbox
                            rounded={null}
                            className="cell-checkbox no-full-width no-border"
                            checked={active.value}
                            onChange={() => {
                                active.setValue(!active.value);
                            }}
                            label={t('active')}
                            data-testid="active-checkbox"
                            size="medium"
                        />
                    </div>
                    <ItemList
                        id="add-risk-list"
                        items={filteredStudies}
                        textField="studyNumber"
                        filter={filter}
                        multiselect
                        selectedItems={selectedStudies}
                        onSelectionChange={setSelectedStudies}
                        onIconClick={handleIsAdmin}
                    />
                </>
            </DialogContent>

            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                <div className="selected-items-container mb-20">
                    <p>
                        {selectedStudies.length}/{filteredStudies?.length}
                    </p>
                </div>
                <Button
                    text="common:update"
                    disabled={!isEnabled || saving.value || !canEdit || disabledSave.value}
                    id="add-study-save-close"
                    onClick={() => assignItems()}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default StudyMembership;
